// Shared between DocOutline (TOC panel) and Markdown.tsx (heading renderer):
// IDs must match in both places, so both call sites use buildOutline / slugify
// with the same dedupe counter and walk headings in source order.
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";

const parser = unified().use(remarkParse).use(remarkGfm);
type MarkdownNode = { type: string; depth?: number; value?: string; children?: MarkdownNode[] };
function headingText(node: MarkdownNode): string {
  // ReactMarkdown renders an image as an element, not heading text; code and
  // escaped inline HTML are text. Link destinations never enter the heading ID.
  if (node.type === "image") return "";
  if (node.children) return node.children.map(headingText).join("");
  return node.value ?? "";
}

export type OutlineItem = { level: 1 | 2 | 3; text: string; id: string };

export function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // diacritics
    .replace(/\p{Extended_Pictographic}/gu, "") // emoji
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

// In-place dedupe — increments the counter map and returns the suffixed id.
// Exposed so Markdown.tsx can share one counter across its h1/h2/h3 overrides
// during a single render and produce the same IDs buildOutline produced.
export function nextHeadingId(
  text: string,
  level: 1 | 2 | 3,
  seen: Map<string, number>
): string {
  let base = slugify(text) || `h${level}`;
  const n = (seen.get(base) ?? 0) + 1;
  seen.set(base, n);
  return n > 1 ? `${base}-${n}` : base;
}

export function buildOutline(md: string): OutlineItem[] {
  const out: OutlineItem[] = [];
  const seen = new Map<string, number>();
  function visit(node: MarkdownNode) {
    if (node.type === "heading" && node.depth && node.depth <= 3) {
      const level = node.depth as 1 | 2 | 3;
      const text = headingText(node).trim();
      out.push({ level, text, id: nextHeadingId(text, level, seen) });
    }
    for (const child of node.children ?? []) visit(child);
  }
  visit(parser.parse(md) as MarkdownNode);
  return out;
}
